import { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { adminApi, Badge, Table, Toolbar, inpCls, btnPrimary } from './_shared';
import { Download, Search, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const ACTION_TONE = {
  user_create: 'green', user_update: 'blue', user_deactivate: 'red', password_reset: 'amber',
  project_update: 'blue', project_delete: 'red',
  module_update: 'blue', role_template_update: 'blue', license_update: 'orange',
  invite_sent: 'green', invite_failed: 'red', login: 'slate', feature_flag_toggle: 'amber',
};

export default function AdminAudit() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ action: '', target_type: '', actor: '', since_days: 30 });

  const load = async () => {
    setLoading(true);
    try { const r = await adminApi.audit(filters); setEvents(r.events || []); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [filters.action, filters.target_type, filters.actor, filters.since_days]);

  const downloadCsv = async () => {
    try {
      const res = await api.get('/admin/audit/export', { params: { since_days: filters.since_days }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `audit_${filters.since_days}d.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const cols = [
    { key: 'ts', header: 'Timestamp', render: r => <span className="text-[12px] text-slate-400 tabular-nums font-mono">{(r.ts || '').slice(0, 19).replace('T', ' ')}</span>, thStyle: { width: 170 } },
    { key: 'actor', header: 'Actor', render: r => <span className="text-slate-300 text-xs">{r.actor_email || r.actor_user_id || '—'}</span> },
    { key: 'action', header: 'Action', render: r => <Badge tone={ACTION_TONE[r.action] || 'slate'}>{r.action}</Badge> },
    { key: 'target', header: 'Target', render: r => (
      <div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500">{r.target_type}</div>
        <div className="text-xs text-slate-400 font-mono">{r.target_id}</div>
      </div>
    )},
    { key: 'meta', header: 'Details', render: r => <code className="text-[11px] text-slate-500 break-all">{JSON.stringify(r.meta || {}).slice(0, 120)}</code> },
  ];

  return (
    <AdminLayout title="Audit Log" subtitle="Admin · Compliance Trail"
      actions={<button onClick={downloadCsv} className={btnPrimary} data-testid="audit-export-btn"><Download className="w-3.5 h-3.5"/>Export CSV</button>}>
      <Toolbar>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500"/>
          <input className={`${inpCls} pl-9 w-full`} placeholder="Filter by actor email…" value={filters.actor} onChange={e => setFilters(f => ({...f, actor: e.target.value}))} data-testid="audit-actor"/>
        </div>
        <select className={inpCls} value={filters.action} onChange={e => setFilters(f => ({...f, action: e.target.value}))}>
          <option value="">All actions</option>
          {Object.keys(ACTION_TONE).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className={inpCls} value={filters.target_type} onChange={e => setFilters(f => ({...f, target_type: e.target.value}))}>
          <option value="">All targets</option>
          <option value="user">User</option>
          <option value="project">Project</option>
          <option value="module">Module</option>
          <option value="role">Role</option>
          <option value="flag">Feature flag</option>
          <option value="license">License</option>
        </select>
        <select className={inpCls} value={filters.since_days} onChange={e => setFilters(f => ({...f, since_days: Number(e.target.value)}))}>
          <option value={1}>Last 24 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <button onClick={load} className="ml-auto inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-400 hover:text-slate-100 px-3 py-1.5 rounded transition">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`}/>Refresh
        </button>
      </Toolbar>
      <Table columns={cols} rows={events} empty={loading ? 'Loading…' : 'No audit events match.'} getKey={r => r.audit_id || `${r.ts}-${r.action}`}/>
    </AdminLayout>
  );
}
